import { useState, useEffect, useRef, useMemo } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, writeBatch } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { db } from '../../config/firebaseConfig';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { useTranslation } from '../../i18n/LanguageContext';
import { STATUS_KEYS, pick } from './budgetColumns';

// --- Updated Helper Function for Statistics ---
const calculateStatistics = (budgetsData) => {
  let pendingExpense = 0;
  let paidExpense = 0;
  let receivedIncome = 0;
  let pendingIncome = 0;

  // Filter for active items first
  const activeBudgets = budgetsData.filter(budget => budget.AktifDurum !== 'Pasif');

  activeBudgets.forEach(budget => {
    const amount = parseFloat(budget['BütçeMiktarı'] || 0);
    
    if (budget['GiderTipi'] === 'Gelir') {
      // Income Calculation
      if (budget['ÖdemeDurumu'] === 'Alındı') {
        receivedIncome += amount;
      } else if (budget['ÖdemeDurumu'] === 'Beklemede') {
        pendingIncome += amount;
      }
    } else {
      // Expense Calculation
      if (budget['ÖdemeDurumu'] === 'Ödendi') {
        paidExpense += amount;
      } else if (budget['ÖdemeDurumu'] === 'Beklemede') {
        pendingExpense += amount;
      }
    }
  });

  return { 
    activeTotalRecords: activeBudgets.length, // Count only active records
    pendingExpense,
    paidExpense,
    receivedIncome,
    pendingIncome
  };
};
// --- End Updated Helper Function ---

/**
 * Gelir & Gider Yönetimi'nin veri mantığı: Budgets ve çalışan okuma, arama,
 * sütun ve tarih süzgeçleri, ödeme / aktif durumu değiştirme, silme, "Tümünü
 * Sil (Test)", istatistikler ve Excel çıktısı (Türkçe). Eski sayfadaki kod
 * birebir taşındı; bildirimler çevrildi ve tablo yüklenirken bir bayrak eklendi.
 */
const useBudgets = () => {
  const { t } = useTranslation();
  // Toasts read the current language through a ref, so switching it never refetches
  const labelRef = useRef(null);
  labelRef.current = (key, vars) => t(`budget.${key}`, vars);
  const msg = (key, vars) => labelRef.current(`toast.${key}`, vars);
  const statusText = (value) => (STATUS_KEYS[value] ? labelRef.current(`status.${STATUS_KEYS[value]}`) : value);
  const { userAuthorization } = useUserProfile();
  const isAdmin = userAuthorization.includes('admin') || userAuthorization.includes('Kısıtlı Admin');
  const [budgets, setBudgets] = useState([]);
  const [activeTab, setActiveTab] = useState('gelir');
  const [filteredIncome, setFilteredIncome] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [filters, setFilters] = useState({});
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [isAddIncomeModalOpen, setIsAddIncomeModalOpen] = useState(false);
  const [isAddExpenseModalOpen, setIsAddExpenseModalOpen] = useState(false);
  const [isImportIncomeModalOpen, setIsImportIncomeModalOpen] = useState(false);
  const [isImportExpenseModalOpen, setIsImportExpenseModalOpen] = useState(false);
  const [isEditIncomeModalOpen, setIsEditIncomeModalOpen] = useState(false);
  const [isEditExpenseModalOpen, setIsEditExpenseModalOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [employees, setEmployees] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteAllConfirm, setDeleteAllConfirm] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const [activeTotalRecords, setActiveTotalRecords] = useState(0);
  const [pendingBudgetAmount, setPendingBudgetAmount] = useState(0);
  const [paidBudgetAmount, setPaidBudgetAmount] = useState(0);
  const [receivedIncomeAmount, setReceivedIncomeAmount] = useState(0);
  const [pendingIncomeAmount, setPendingIncomeAmount] = useState(0);

  const fetchBudgets = async () => {
    setIsLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'Budgets'));
      const budgetsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Use helper function to calculate stats
      const stats = calculateStatistics(budgetsData);
      setActiveTotalRecords(stats.activeTotalRecords);
      setPendingBudgetAmount(stats.pendingExpense);
      setPaidBudgetAmount(stats.paidExpense);
      setReceivedIncomeAmount(stats.receivedIncome);
      setPendingIncomeAmount(stats.pendingIncome);
      
      setBudgets(budgetsData);
      
    } catch (error) {
      console.error('Error fetching budgets:', error);
      toast.error(msg('loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Employees'));
      const employeesData = {};
      querySnapshot.docs.forEach(doc => {
        employeesData[doc.id] = doc.data();
      });
      setEmployees(employeesData);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error(msg('employeesError'));
    }
  };

  useEffect(() => {
    fetchBudgets();
    fetchEmployees();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { derivedIncome, derivedExpenses } = useMemo(() => {
    let filtered = budgets;

    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      const searchFields = [
        'BütçeAdı', 'BütçeKaynak', 'BütçeSahibi', 'GiderTipi',
        'GibNumarasi', 'Ettn', 'ZarfUuid', 'Senaryo', 'FaturaTipi',
        'MusteriUnvan', 'MusteriTcknVkn', 'MusteriEtiketi',
        'GondericiUnvan', 'GondericiTcknVkn', 'GondericiEtiketi',
      ];
      filtered = filtered.filter(budget =>
        searchFields.some(f => budget[f] && String(budget[f]).toLowerCase().includes(lowercasedSearch))
      );
    }

    // Some displayed columns fall back to another field when the primary is empty.
    const FILTER_FALLBACKS = {
      MusteriUnvan: ['MusteriUnvan', 'BütçeKaynak'],
      GondericiUnvan: ['GondericiUnvan', 'BütçeKaynak'],
      BelgeTarihi: ['BelgeTarihi', 'BütçeTarihi'],
      Tutar: ['Tutar', 'BütçeMiktarı'],
    };

    Object.keys(filters).forEach(key => {
      const filterValue = filters[key];
      if (filterValue === '' || filterValue === null || filterValue === undefined) return;
      const sourceKeys = FILTER_FALLBACKS[key] || [key];
      if (key === 'ÖdemeDurumu' || key === 'ParaBirimi' || key === 'Senaryo' ||
          key === 'FaturaTipi' || key === 'ZarfDurumu' || key === 'MusteriUnvan' ||
          key === 'GondericiUnvan') {
        filtered = filtered.filter(budget => {
          const v = sourceKeys.map(k => budget[k]).find(x => x !== undefined && x !== null && x !== '');
          return v === filterValue;
        });
      } else {
        const needle = String(filterValue).toLowerCase();
        filtered = filtered.filter(budget => {
          const v = sourceKeys.map(k => budget[k]).find(x => x !== undefined && x !== null && x !== '');
          return v != null && String(v).toLowerCase().includes(needle);
        });
      }
    });

    if (startDateFilter) {
      filtered = filtered.filter(budget => budget['BütçeTarihi'] && budget['BütçeTarihi'] >= startDateFilter);
    }
    if (endDateFilter) {
      filtered = filtered.filter(budget => budget['BütçeTarihi'] && budget['BütçeTarihi'] <= endDateFilter);
    }

    return {
      derivedIncome: filtered.filter(b => b['GiderTipi'] === 'Gelir'),
      derivedExpenses: filtered.filter(b => b['GiderTipi'] !== 'Gelir'),
    };
  }, [budgets, filters, searchTerm, startDateFilter, endDateFilter]);

  useEffect(() => {
    setFilteredIncome(derivedIncome);
    setFilteredExpenses(derivedExpenses);
    if (selectedBudget && !derivedIncome.find(b => b.id === selectedBudget.id) && !derivedExpenses.find(b => b.id === selectedBudget.id)) {
      setSelectedBudget(null);
    }
  }, [derivedIncome, derivedExpenses]); // selectedBudget intentionally excluded — checking presence only

  const handleFilter = (column, value) => {
    const newFilters = { ...filters };
    if (value === '' || value === null || value === undefined) {
        delete newFilters[column];
    } else {
        newFilters[column] = value;
    }
    setFilters(newFilters);
    setSelectedBudget(null);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setStartDateFilter('');
    setEndDateFilter('');
    setSelectedBudget(null);
  };

  const downloadExcel = () => {
    try {
      const combinedData = [...filteredIncome, ...filteredExpenses];
      
      if (combinedData.length === 0) {
          toast.warn(msg('nothingToExport'));
          return;
      }

      const dataToExport = combinedData.map(budget => {
        const isIncome = budget['GiderTipi'] === 'Gelir';
        return {
          'Tip': isIncome ? 'Gelir' : 'Gider',
          'GİB NUMARASI': budget['GibNumarasi'] || '',
          'TASLAK NO': budget['TaslakNo'] || '',
          'SENARYO': budget['Senaryo'] || '',
          'FATURA TİPİ': budget['FaturaTipi'] || '',
          'OLUŞTURMA TARİHİ': budget['OlusturmaTarihi'] || '',
          'OLUŞTURMA ZAMANI': budget['OlusturmaZamani'] || '',
          'BELGE TARİHİ': budget['BelgeTarihi'] || budget['BütçeTarihi'] || '',
          'BELGE ZAMANI': budget['BelgeZamani'] || '',
          'TCKN/VKN': isIncome ? (budget['MusteriTcknVkn'] || '') : (budget['GondericiTcknVkn'] || ''),
          'UNVAN': isIncome ? (budget['MusteriUnvan'] || budget['BütçeKaynak'] || '') : (budget['GondericiUnvan'] || budget['BütçeKaynak'] || ''),
          'ETİKET': isIncome ? (budget['MusteriEtiketi'] || '') : (budget['GondericiEtiketi'] || ''),
          'TUTAR': budget['Tutar'] ?? budget['BütçeMiktarı'] ?? '',
          'PARA BİRİMİ': budget['ParaBirimi'] || '',
          'ZARF UUID': budget['ZarfUuid'] || '',
          'ZARF DURUMU': budget['ZarfDurumu'] || '',
          'ETTN': budget['Ettn'] || '',
          'İSKONTO': budget['Iskonto'] || 0,
          'FATURA DIŞI MASRAFLARI': budget['FaturaDisiMasraflari'] || 0,
          'SATIR SAYISI': budget['SatirSayisi'] || 0,
          'BİRİM FİYAT * MİKTAR TUTARI': budget['BirimFiyatMiktarTutari'] || 0,
          'ÖDENECEK TUTAR': budget['OdenecekTutar'] || 0,
          'VERGİLER HARİÇ TUTAR': budget['VergilerHaricTutar'] || 0,
          'VERGİLER DAHİL TUTAR': budget['VergilerDahilTutar'] || 0,
          'ÖDEME DURUMU': budget['ÖdemeDurumu'] || ''
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'GelirGider');
      XLSX.writeFile(workbook, 'GelirGiderListesi.xlsx');
      
      toast.success(msg('excelDone'));
    } catch (error) {
      console.error('Error downloading Excel:', error);
      toast.error(msg('excelError'));
    }
  };

  const handleAddItem = async (itemData) => { 
    try {
      console.log('New item data:', itemData);
      setIsAddIncomeModalOpen(false);
      setIsAddExpenseModalOpen(false);
      await fetchBudgets();
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error(msg('addError'));
    }
  };
  
  const handleBudgetSelect = (budget) => {
      if (selectedBudget && selectedBudget.id === budget.id) {
          setSelectedBudget(null);
      } else {
    setSelectedBudget(budget);
      }
  };
  
  const handleToggleStatus = async () => {
      if (!selectedBudget) return;

      const currentStatus = selectedBudget['ÖdemeDurumu'];
      const isIncome = selectedBudget['GiderTipi'] === 'Gelir';
      let newStatus = '';

      if (isIncome) {
          if (currentStatus === 'Alındı') newStatus = 'Beklemede';
          else if (currentStatus === 'Beklemede') newStatus = 'Alındı';
      } else {
          if (currentStatus === 'Ödendi') newStatus = 'Beklemede';
          else if (currentStatus === 'Beklemede') newStatus = 'Ödendi';
      }

      if (!newStatus) {
          toast.warn(msg('statusLocked'));
          return;
      }

      const budgetRef = doc(db, 'Budgets', selectedBudget.id);
      
      try {
          await updateDoc(budgetRef, {
              ÖdemeDurumu: newStatus
          });
          toast.success(msg('statusUpdated', { status: statusText(newStatus) }));
          const updatedBudgets = budgets.map(b => 
              b.id === selectedBudget.id ? { ...b, ÖdemeDurumu: newStatus } : b
          );
          setBudgets(updatedBudgets);

          setSelectedBudget({ ...selectedBudget, ÖdemeDurumu: newStatus });

          const updatedStats = calculateStatistics(updatedBudgets);
          setActiveTotalRecords(updatedStats.activeTotalRecords);
          setPendingBudgetAmount(updatedStats.pendingExpense);
          setPaidBudgetAmount(updatedStats.paidExpense);
          setReceivedIncomeAmount(updatedStats.receivedIncome);
          setPendingIncomeAmount(updatedStats.pendingIncome);

      } catch (error) {
          console.error("Error updating status: ", error);
          toast.error(msg('statusError'));
      }
  };
  
  const handleEditBudget = () => {
    if (!selectedBudget) {
      toast.warn(msg('selectToEdit'));
      return;
    }
    setEditingBudget(selectedBudget);

    if (selectedBudget['GiderTipi'] === 'Gelir') {
      setIsEditIncomeModalOpen(true);
    } else {
      setIsEditExpenseModalOpen(true);
    }
  };
  
  const handleDeleteBudget = () => {
    if (!selectedBudget) return;
    setDeleteConfirm(true);
  };

  const confirmDeleteAll = async () => {
    setIsDeletingAll(true);
    try {
      const snap = await getDocs(collection(db, 'Budgets'));
      const docs = snap.docs;
      if (!docs.length) {
        toast.info(msg('nothingToDelete'));
        setDeleteAllConfirm(false);
        setIsDeletingAll(false);
        return;
      }
      const chunkSize = 400;
      for (let i = 0; i < docs.length; i += chunkSize) {
        const batch = writeBatch(db);
        docs.slice(i, i + chunkSize).forEach(d => batch.delete(d.ref));
        await batch.commit();
      }
      toast.success(msg('deletedAll', { count: docs.length }));
      setBudgets([]);
      setSelectedBudget(null);
      setActiveTotalRecords(0);
      setPendingBudgetAmount(0);
      setPaidBudgetAmount(0);
      setReceivedIncomeAmount(0);
      setPendingIncomeAmount(0);
      setDeleteAllConfirm(false);
    } catch (err) {
      console.error('Delete all error:', err);
      toast.error(msg('deleteAllError'));
    } finally {
      setIsDeletingAll(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedBudget) return;
    try {
      await deleteDoc(doc(db, 'Budgets', selectedBudget.id));
      const updatedBudgets = budgets.filter(b => b.id !== selectedBudget.id);
      setBudgets(updatedBudgets);
      setSelectedBudget(null);
      setDeleteConfirm(false);
      const updatedStats = calculateStatistics(updatedBudgets);
      setActiveTotalRecords(updatedStats.activeTotalRecords);
      setPendingBudgetAmount(updatedStats.pendingExpense);
      setPaidBudgetAmount(updatedStats.paidExpense);
      setReceivedIncomeAmount(updatedStats.receivedIncome);
      setPendingIncomeAmount(updatedStats.pendingIncome);
      toast.success(msg('deleted'));
    } catch (error) {
      console.error('Error deleting budget:', error);
      toast.error(msg('deleteError'));
    }
  };
  
  const handleUpdateBudget = (budgetId, updatedData) => {
      const updatedBudgets = budgets.map(b => 
          b.id === budgetId ? { ...b, ...updatedData, id: budgetId } : b
      );
      setBudgets(updatedBudgets);

      if (selectedBudget && selectedBudget.id === budgetId) {
          setSelectedBudget({ ...selectedBudget, ...updatedData, id: budgetId });
      }

      const updatedStats = calculateStatistics(updatedBudgets);
      setActiveTotalRecords(updatedStats.activeTotalRecords);
      setPendingBudgetAmount(updatedStats.pendingExpense);
      setPaidBudgetAmount(updatedStats.paidExpense);
      setReceivedIncomeAmount(updatedStats.receivedIncome);
      setPendingIncomeAmount(updatedStats.pendingIncome);
  };

  const handleToggleActiveStatus = async () => {
    if (!selectedBudget) return;

    const currentActiveStatus = selectedBudget.AktifDurum === 'Pasif' ? 'Pasif' : 'Aktif'; 
    const newActiveStatus = currentActiveStatus === 'Aktif' ? 'Pasif' : 'Aktif';

    const budgetRef = doc(db, 'Budgets', selectedBudget.id);

    try {
        await updateDoc(budgetRef, {
            AktifDurum: newActiveStatus
        });
        toast.success(msg('activeUpdated', { status: statusText(newActiveStatus) }));

        const updatedBudgets = budgets.map(b => 
            b.id === selectedBudget.id ? { ...b, AktifDurum: newActiveStatus } : b
        );
        setBudgets(updatedBudgets);

        setSelectedBudget({ ...selectedBudget, AktifDurum: newActiveStatus });

        const updatedStats = calculateStatistics(updatedBudgets);
        setActiveTotalRecords(updatedStats.activeTotalRecords);
        setPendingBudgetAmount(updatedStats.pendingExpense);
        setPaidBudgetAmount(updatedStats.paidExpense);
        setReceivedIncomeAmount(updatedStats.receivedIncome);
        setPendingIncomeAmount(updatedStats.pendingIncome);

    } catch (error) {
        console.error("Error updating active status: ", error);
        toast.error(msg('activeError'));
    }
  };

  const canToggleStatus = selectedBudget && 
      ((selectedBudget['GiderTipi'] === 'Gelir' && (selectedBudget['ÖdemeDurumu'] === 'Alındı' || selectedBudget['ÖdemeDurumu'] === 'Beklemede')) ||
       (selectedBudget['GiderTipi'] !== 'Gelir' && (selectedBudget['ÖdemeDurumu'] === 'Ödendi' || selectedBudget['ÖdemeDurumu'] === 'Beklemede')));

  const isAnyFilterActive = searchTerm || Object.values(filters).some(v => v !== '') || startDateFilter || endDateFilter;

  // Determine active status for the selected budget (defaulting to Aktif)
  const isSelectedBudgetActive = !(selectedBudget?.AktifDurum === 'Pasif');

  const uniq = (arr, key) =>
    Array.from(new Set(arr.map(i => i[key]).filter(v => v !== undefined && v !== null && v !== ''))).map(v => ({ value: v, label: String(v) }));

  const incomeUniqueValues = useMemo(() => ({
    Senaryo: uniq(filteredIncome, 'Senaryo'),
    FaturaTipi: uniq(filteredIncome, 'FaturaTipi'),
    MusteriUnvan: uniq(filteredIncome.map(r => ({ ...r, MusteriUnvan: pick(r, 'MusteriUnvan', 'BütçeKaynak') })), 'MusteriUnvan'),
    ParaBirimi: uniq(filteredIncome, 'ParaBirimi'),
    ZarfDurumu: uniq(filteredIncome, 'ZarfDurumu'),
    ÖdemeDurumu: uniq(filteredIncome, 'ÖdemeDurumu'),
  }), [filteredIncome]);

  const expenseUniqueValues = useMemo(() => ({
    Senaryo: uniq(filteredExpenses, 'Senaryo'),
    FaturaTipi: uniq(filteredExpenses, 'FaturaTipi'),
    GondericiUnvan: uniq(filteredExpenses.map(r => ({ ...r, GondericiUnvan: pick(r, 'GondericiUnvan', 'BütçeKaynak') })), 'GondericiUnvan'),
    ParaBirimi: uniq(filteredExpenses, 'ParaBirimi'),
    ZarfDurumu: uniq(filteredExpenses, 'ZarfDurumu'),
    ÖdemeDurumu: uniq(filteredExpenses, 'ÖdemeDurumu'),
  }), [filteredExpenses]);

  // Add function to get employee name
  const getEmployeeName = (employeeId) => {
    if (!employeeId || !employees[employeeId]) return employeeId;
    const employee = employees[employeeId];
    return `${employee.eName} ${employee.eSurname}`;
  };

  return {
    isAdmin, isLoading, budgets, employees, activeTab, setActiveTab,
    filteredIncome, filteredExpenses, filters, handleFilter, clearFilters,
    searchTerm, setSearchTerm, startDateFilter, setStartDateFilter, endDateFilter, setEndDateFilter, isAnyFilterActive,
    activeTotalRecords, pendingBudgetAmount, paidBudgetAmount, receivedIncomeAmount, pendingIncomeAmount,
    selectedBudget, setSelectedBudget, handleBudgetSelect, canToggleStatus, isSelectedBudgetActive,
    handleToggleStatus, handleEditBudget, handleDeleteBudget, handleToggleActiveStatus,
    deleteConfirm, setDeleteConfirm, confirmDelete, deleteAllConfirm, setDeleteAllConfirm, confirmDeleteAll, isDeletingAll,
    isAddIncomeModalOpen, setIsAddIncomeModalOpen, isAddExpenseModalOpen, setIsAddExpenseModalOpen,
    isImportIncomeModalOpen, setIsImportIncomeModalOpen, isImportExpenseModalOpen, setIsImportExpenseModalOpen,
    isEditIncomeModalOpen, setIsEditIncomeModalOpen, isEditExpenseModalOpen, setIsEditExpenseModalOpen,
    editingBudget, setEditingBudget, handleAddItem, handleUpdateBudget,
    incomeUniqueValues, expenseUniqueValues, getEmployeeName,
    fetchBudgets, downloadExcel,
  };
};

export default useBudgets;
